// src/hooks/useSupabaseAuth.ts
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

type UiUser = {
  id: string;
  email: string | null;
  name: string;
  plan: 'free' | 'pro' | 'agency' | string;
} | null;

type SetUserFn = (u: UiUser) => void;

export function useSupabaseAuth(setUser: SetUserFn) {
  const [session, setSession] = useState<any>(null);

  const upsertProfile = async (sbUser: any) => {
    const name =
      (sbUser.user_metadata &&
        (sbUser.user_metadata.full_name || sbUser.user_metadata.name)) ||
      (sbUser.email ? sbUser.email.split('@')[0] : 'Usuario');

    await supabase.from('profiles').upsert({
      id: sbUser.id,
      email: sbUser.email,
      name,
    });
  };

  const refreshUserPlan = async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('plan, name')
      .eq('id', userId)
      .single();
    if (!error && data) {
      setUser(prev =>
        prev ? { ...prev, plan: data.plan || 'free', name: data.name || prev.name } : prev
      );
    }
  };

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setSession(session || null);

      if (session?.user) {
        const sbUser = session.user;
        await upsertProfile(sbUser);
        setUser({
          id: sbUser.id,
          email: sbUser.email,
          name:
            (sbUser.user_metadata &&
              (sbUser.user_metadata.full_name || sbUser.user_metadata.name)) ||
            (sbUser.email ? sbUser.email.split('@')[0] : 'Usuario'),
          plan: 'free',
        });
        await refreshUserPlan(sbUser.id);
      } else {
        setUser(null);
      }
    };

    init();

    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session || null);

      if (session?.user) {
        const sbUser = session.user;
        await upsertProfile(sbUser);
        setUser({
          id: sbUser.id,
          email: sbUser.email,
          name:
            (sbUser.user_metadata &&
              (sbUser.user_metadata.full_name || sbUser.user_metadata.name)) ||
            (sbUser.email ? sbUser.email.split('@')[0] : 'Usuario'),
          plan: 'free',
        });
        await refreshUserPlan(sbUser.id);
      } else {
        setUser(null);
      }
    });

    return () => {
      sub.subscription.unsubscribe();
    };
  }, [setUser]);

  const loginWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
    if (error) {
      console.error('❌ Error Google Auth:', error);
      alert('Error con Google Auth: ' + error.message);
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
  };

  return { session, loginWithGoogle, logout, refreshUserPlan };
}
